// Migrated 1:1 from @nexato/nx-core-module (timeEntry/components/timeEntry-category-accordion, #686)
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TimeEntry } from '../../entities/timeEntry/timeEntry';
import { TimeEntryCategorySum } from '../../entities/timeEntryCategorySum/timeEntryCategorySum';
import { TimeEntryService } from '../../services/timeEntry/timeEntryService';

@Component({
  selector: 'app-timeEntry-category-accordion',
  templateUrl: './timeEntry-category-accordion.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TimeEntryCategoryAccordionComponent {

  // Injected services
  private readonly destroyRef = inject(DestroyRef);
  private readonly timeEntryService = inject(TimeEntryService);

  // Inputs
  readonly categorySum = input.required<TimeEntryCategorySum>();
  readonly ownerId = input.required<string>();
  readonly ownerType = input.required<string>();
  readonly totalDuration = input.required<number>(); // Für Prozentberechnung

  // State
  readonly isExpanded = signal(false);
  readonly timeEntries = signal<TimeEntry[]>([]);
  readonly currentPage = signal(0);
  readonly totalElements = signal(0);
  readonly pageSize = 10;

  // Computed
  readonly totalPages = computed(() =>
    Math.ceil(this.totalElements() / this.pageSize)
  );
  readonly hasNextPage = computed(() =>
    this.currentPage() < this.totalPages() - 1
  );
  readonly hasPreviousPage = computed(() =>
    this.currentPage() > 0
  );
  readonly percentage = computed(() => {
    const total = this.totalDuration();
    if (total === 0) return 0;
    return (this.categorySum().sum / total) * 100;
  });

  constructor() {
    // Laden beim Aufklappen
    effect(() => {
      if (this.isExpanded()) {
        this.loadTimeEntries();
      }
    });
  }

  toggle(): void {
    this.isExpanded.update(expanded => !expanded);
  }

  private loadTimeEntries(): void {
    this.timeEntryService.getTimeEntriesPagedWithPagination({
      ownerId: this.ownerId(),
      ownerType: this.ownerType(),
      categoryId: this.categorySum().category.id,
      pageNumber: this.currentPage(),
      pageSize: this.pageSize,
      sortProperty: 'startDateTime',
      sortDirection: 'desc'
    }).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(result => {
      this.timeEntries.set(result.content || []);
      this.totalElements.set(result.totalElements || 0);
    });
  }

  nextPage(): void {
    if (this.hasNextPage()) {
      this.currentPage.update(page => page + 1);
      this.loadTimeEntries();
    }
  }

  previousPage(): void {
    if (this.hasPreviousPage()) {
      this.currentPage.update(page => page - 1);
      this.loadTimeEntries();
    }
  }

  getPageInfo(): string {
    const start = this.currentPage() * this.pageSize + 1;
    const end = Math.min((this.currentPage() + 1) * this.pageSize, this.totalElements());
    return `${start}-${end} von ${this.totalElements()}`;
  }

  formatDurationToStd(seconds: number): string {
    if (!seconds && seconds !== 0) return '00:00 h';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const formattedHours = hours.toString().padStart(2, '0');
    const formattedMinutes = minutes.toString().padStart(2, '0');
    return `${formattedHours}:${formattedMinutes} h`;
  }
}
